// Main classes page: sessions, certificates, and verification tabs
import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { GraduationCap, Plus, Award, ShieldCheck, RotateCcw, Loader2, AlertCircle, Radio, Clock, CheckCircle2 } from 'lucide-react'

import { Card, CardContent } from '@/ui/card'
import { Button } from '@/ui/button'
import { useClassSessionStore } from '@/features/classes/stores'
import { useIsMounted } from '@/core/hooks/useIsMounted'
import { cn } from '@/core/lib/utils'

import { HelpButton } from './components/HelpButton'
import { CreateSessionModal } from './components/CreateSessionModal'
import { CertificatesTab } from './components/CertificatesTab'
import { VerifyPanel } from './components/VerifyPanel'
import { SessionCard } from './components/SessionCard'

type Tab = 'sessions' | 'certificates' | 'verify'

export function ClassesPage() {
  const mounted = useIsMounted()
  const navigate = useNavigate()
  const sessions = useClassSessionStore((s) => s.sessions)
  const loadSessions = useClassSessionStore((s) => s.loadSessions)
  const loadSessionAttendees = useClassSessionStore((s) => s.loadSessionAttendees)
  const isLoading = useClassSessionStore((s) => s.isLoading)
  const error = useClassSessionStore((s) => s.error)

  const [activeTab, setActiveTab] = useState<Tab>('sessions')
  const [showCreate, setShowCreate] = useState(false)

  const activeSession = sessions.find((s) => s.status === 'active' || s.status === 'checking')
  useEffect(() => {
    if (!activeSession || activeTab !== 'sessions') {return}
    void (async () => {
      if (!mounted.current) {return}
      try {
        await loadSessionAttendees(activeSession.id)
      } catch {
        // Error already logged by the attendee store
      }
    })()
  }, [activeSession, activeTab, loadSessionAttendees, mounted])

  // Today in the project's timezone (America/Vancouver)
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Vancouver' })
  const hasClassToday = sessions.some((s) => s.date === todayStr)

  // Hide stale draft sessions whose date has already passed — unless they have
  // registered attendees, which still need to be managed (e.g. close the session
  // and issue certificates). Past active/checking sessions are still visible so
  // instructors can close them; closed sessions remain in the Completed section.
  const visibleSessions = sessions.filter(
    (s) => s.status !== 'draft' || s.date >= todayStr || (s.attendeeCount ?? 0) > 0
  )

  // Sort sessions by date: upcoming/active/checking soonest first, closed most recent last
  const sortedSessions = visibleSessions.slice().sort((a, b) => {
    const closedA = a.status === 'closed'
    const closedB = b.status === 'closed'
    if (closedA !== closedB) {return closedA ? 1 : -1}
    if (closedA) {return b.date.localeCompare(a.date)}
    return a.date.localeCompare(b.date)
  })

  const todaySessions = sortedSessions.filter((s) => s.date === todayStr)
  const otherSessions = sortedSessions.filter((s) => s.date !== todayStr)

  const activeCount = sessions.filter((s) => s.status === 'active' || s.status === 'checking').length
  const draftCount = sessions.filter(
    (s) => s.status === 'draft' && s.date >= todayStr
  ).length
  const closedCount = sessions.filter((s) => s.status === 'closed').length

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'sessions',     label: 'My Classes',            icon: GraduationCap },
    { id: 'certificates', label: 'Certificates',          icon: Award },
    { id: 'verify',       label: 'Verify Certificate',    icon: ShieldCheck },
  ]


  return (
    <div className="space-y-4 md:space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-primary" />
            In-Person Classes
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Schedule sessions, manage students, and issue certificates
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <HelpButton />
          {activeTab === 'sessions' && (
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4" />
              New Class
            </Button>
          )}
        </div>
      </div>

      {/* Status summary cards */}
      {activeTab === 'sessions' && sessions.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
              <Radio className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-green-700 leading-none">{activeCount}</p>
              <p className="text-xs text-green-600 mt-0.5">In Progress</p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-amber-700 leading-none">{draftCount}</p>
              <p className="text-xs text-amber-600 mt-0.5">Upcoming</p>
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-slate-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-700 leading-none">{closedCount}</p>
              <p className="text-xs text-slate-600 mt-0.5">Completed</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-slate-150 overflow-x-auto">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              activeTab === id
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-500 hover:text-navy'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Sessions tab */}
      {activeTab === 'sessions' && (
        <div className="space-y-3">
          {!isLoading && !error && sessions.length > 0 && !hasClassToday && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center">
              <p className="text-sm text-slate-600">No classes today</p>
            </div>
          )}
          {isLoading ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Loader2 className="w-8 h-8 text-slate-150 mx-auto mb-3 animate-spin" />
                <p className="text-slate-400 text-sm">Loading classes…</p>
              </CardContent>
            </Card>
          ) : error ? (
            <Card>
              <CardContent className="py-8 text-center">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <p className="text-red-500 text-sm font-semibold mb-1">Could not load classes</p>
                <p className="text-slate-500 text-xs mb-5 max-w-xs mx-auto font-mono bg-slate-50 rounded px-2 py-1">{error}</p>
                <div className="text-left max-w-sm mx-auto bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900">
                  <p className="font-semibold mb-1.5">Steps to fix:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Verify the <strong>sessions</strong> endpoint is available</li>
                    <li>Check authentication permissions</li>
                    <li>Verify the JWT token is valid</li>
                    <li>Save and click <strong>Retry</strong> again</li>
                  </ol>
                </div>
                <Button variant="outline" size="sm" onClick={() => loadSessions()}>
                  <RotateCcw className="w-3.5 h-3.5" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : sessions.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <GraduationCap className="w-12 h-12 text-slate-150 mx-auto mb-4" />
                <p className="text-slate-400 text-sm font-medium mb-1">No classes scheduled yet.</p>
                <p className="text-slate-400 text-xs mb-4">Click the button to schedule your first class.</p>
                <Button className="mt-2" onClick={() => setShowCreate(true)}>
                  <Plus className="w-4 h-4" />
                  Schedule First Class
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {todaySessions.length > 0 && (
                <div className="space-y-3">
                  {todaySessions.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      onDetails={() => void navigate({ to: '/classes/$sessionId', params: { sessionId: session.id } })}
                    />
                  ))}
                </div>
              )}
              {todaySessions.length > 0 && otherSessions.length > 0 && (
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <span className="relative bg-white px-3 text-xs text-slate-400 mx-auto block w-max">
                    Upcoming
                  </span>
                </div>
              )}
              {otherSessions.length > 0 && (
                <div className="space-y-3">
                  {otherSessions.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      onDetails={() => void navigate({ to: '/classes/$sessionId', params: { sessionId: session.id } })}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Certificates tab */}
      {activeTab === 'certificates' && <CertificatesTab />}

      {/* Verify tab */}
      {activeTab === 'verify' && <VerifyPanel />}

      {/* Modals */}
      {showCreate && <CreateSessionModal onClose={() => setShowCreate(false)} />}
    </div>
  )
}
